import { ChangeDetectionStrategy, Component, inject, OnDestroy } from "@angular/core";
import { AutoStakingFacade } from "@modules/staking/domain/+state/auto-staking.facade";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { combineLatest, Observable, of, switchMap } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { GetStakingUserJourneyQuery } from "../../../../api/graphql/staking/generated/auto-staking.user-journey.service.graphql-gen";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { EarnConsentScreenFacade } from "@modules/staking/components/earn-consent-screen/earn-consent.screen.facade";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { StakingEntryHelperService } from "@modules/staking/services/staking-entry.helper.service";
import { ActivatedRoute } from "@angular/router";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";

export interface EarnConsentViewModel {
	stakeableId: string;
	terms?: AgreementBundle[];
	hasError?: boolean;
}

@Component({
	selector: "earn-consent-screen",
	templateUrl: "./earn-consent-screen.component.html",
	styleUrl: "./earn-consent-screen.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EarnConsentScreenComponent implements OnDestroy {
	private readonly stakingEntryHelper = inject(StakingEntryHelperService);
	private readonly modalService = inject(UniversalModalService);
	private readonly earnConsentFacade = inject(EarnConsentScreenFacade);
	private readonly autoStakingFacade = inject(AutoStakingFacade);
	private readonly termsService = inject(TermsAndConditionsService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly stakingFacade = inject(StakingFacade);
	public viewModel$ = this.getVm$();

	private userJourney: UserJourney;
	private stakeableId: string;

	public queryParams$ = this.activatedRoute.queryParams;

	public resetViewModel(): void {
		this.viewModel$ = this.getVm$();
	}

	// To remove the background blur when navigatin back from browser back button
	public ngOnDestroy(): void {
		this.modalService.close(ModalCloseReason.close);
	}

	private getVm$(): Observable<EarnConsentViewModel> {
		return combineLatest({
			userJourney: this.autoStakingFacade.getStakingUserJourney$(),
			stakeableId: of(this.earnConsentFacade.viewModel$())
		}).pipe(
			switchMap(({ userJourney, stakeableId }) => {
				this.stakeableId = stakeableId;
				if (userJourney === "error") {
					return [{ terms: [], stakeableId, hasError: true }];
				}
				const uj = userJourney as GetStakingUserJourneyQuery;
				this.userJourney = uj.settings.userJourneys.earnOnStableCoin as unknown as UserJourney;
				return this.termsService.getAgreementBundleData$(this.userJourney, true).pipe(
					map((terms: AgreementBundle[]) => {
						if (terms && terms.length === 0) {
							this.continue();
						}
						return { terms, stakeableId, hasError: false };
					}),
					catchError(() => of({ stakeableId: stakeableId, agreementBundles: [], hasError: true }))
				);
			})
		);
	}

	public cancel(fromADP: boolean): void {
		this.modalService.close(ModalCloseReason.close);
		if (fromADP) {
			return;
		}
		this.stakingEntryHelper.openFlow();
	}

	public acceptTerms(terms: AgreementBundle[]): void {
		const termId = terms[0].agreement_bundle_version_id;
		this.termsService.acceptTerms([this.userJourney], { [termId]: true }, () => {
			this.continue();
		});
	}

	private continue(): void {
		this.stakingFacade.updateStakingProcess({ seenEarnTerms: true });
		this.modalService.close(ModalCloseReason.close, undefined, false, false);
		this.stakingEntryHelper.openFlow(this.stakeableId);
	}
}
